import type { ComputedRef } from 'vue'

import { generatePairingCode, isValidPairingCode, normalizePairingCode } from '~/utils/pairingCode'

export interface GamePresentationOptions<TState> {
  /**
   * Peer-id namespace for this game (e.g. 'showdown'). Determines both the
   * BroadcastChannel name and the peer ID prefix on the signaling server.
   */
  peerNamespace: string
  /**
   * Single character used in the short pairing URL (`/<presenterChar><code>`).
   * Must be unique across all games — collisions would route the wrong game.
   */
  presenterChar: string
  store: GamePresentationStore<TState>
  viewMode: ComputedRef<GameViewMode>
}
export interface GamePresentationStore<TState> {
  applySyncState: (state: TState) => void
  getSyncState: () => TState
}

export type GameViewMode = 'controller' | 'presenter'

export type PresenterTransportKind = 'broadcast' | 'peer' | 'unpaired'

interface FullscreenOptionsWithScreen extends FullscreenOptions {
  screen?: PlacementScreenLike
}

interface PlacementScreenLike {
  availHeight: number
  availLeft: number
  availTop: number
  availWidth: number
  height: number
  left: number
  top: number
  width: number
}

type PresentationMessage<TState>
  = | { state: TState, type: 'sync-state' }
    | { type: 'controller-disconnect' }
    | { type: 'presenter-ready' }

interface ScreenDetailsLike {
  currentScreen: PlacementScreenLike
  screens: PlacementScreenLike[]
}

interface WindowWithPlacement extends Window {
  getScreenDetails?: () => Promise<ScreenDetailsLike>
}

export function useGamePresentation<TState>(options: GamePresentationOptions<TState>) {
  const { peerNamespace, presenterChar, store, viewMode } = options
  const route = useRoute()
  const runtimeConfig = useRuntimeConfig()
  const sharedPresenterWindow = inject(PRESENTER_WINDOW_KEY, null)

  // ── Per-tab identifiers ─────────────────────────────────
  // Pairing code is intentionally NOT persisted — each page load rotates
  // the code so a fresh visit can't be hijacked by a stale presenter still
  // holding the old code. Session ID is per-tab too (popup pairing only).
  const sessionId = ref<string>('')
  const pairingCode = ref<string>('')

  // ── Transport state (BroadcastChannel) ──────────────────
  const channel = shallowRef<BroadcastChannel | null>(null)
  const channelSessionId = ref('')
  const bcPresenterReady = ref(false)

  // ── Presenter-side: which transport are we using? ───────
  const presenterTransportKind = computed<PresenterTransportKind>(() => {
    if (viewMode.value !== 'presenter') {
      return 'unpaired'
    }
    if (typeof route.query.code === 'string' && route.query.code) {
      return 'peer'
    }
    if (typeof route.query.session === 'string' && route.query.session) {
      return 'broadcast'
    }
    return 'unpaired'
  })

  const presenterPeerTargetCode = ref('')
  watchEffect(() => {
    if (viewMode.value === 'presenter' && typeof route.query.code === 'string') {
      presenterPeerTargetCode.value = normalizePairingCode(route.query.code)
    } else {
      presenterPeerTargetCode.value = ''
    }
  })

  // ── PeerJS transport ────────────────────────────────────
  const peerRoleRef = computed<'controller' | 'presenter'>(() => viewMode.value)
  const peerTargetCode = computed(() => {
    if (viewMode.value === 'controller') {
      return pairingCode.value
    }
    return presenterPeerTargetCode.value
  })

  const peerTransport = useGamePeerTransport<TState>({
    onControllerDisconnect: () => {
      // Presenter: controller told us they're leaving — keep listening; they
      // may reopen with the same code momentarily.
      connectedViaPeer.value = false
    },
    onPeerConnected: () => {
      if (viewMode.value === 'controller') {
        // A presenter just joined — send current state immediately so they
        // don't wait for the next store mutation.
        peerTransport.send({ state: store.getSyncState(), type: 'sync-state' })
      } else {
        connectedViaPeer.value = true
      }
    },
    onPeerDisconnected: () => {
      if (viewMode.value === 'presenter') {
        connectedViaPeer.value = false
      }
    },
    onStateReceived: state => {
      if (viewMode.value === 'presenter') {
        store.applySyncState(state)
        connectedViaPeer.value = true
      }
    },
    peerNamespace,
    role: peerRoleRef,
    targetCode: peerTargetCode,
  })

  const connectedViaPeer = ref(false)

  // ── State sync queue (debounced) ────────────────────────
  const connecting = ref(false)
  const lastError = ref<null | string>(null)

  let pendingState: null | TState = null
  let sendTimeout: null | ReturnType<typeof setTimeout> = null
  let presenterHeartbeat: null | ReturnType<typeof setInterval> = null
  let presenterWindowPolling: null | ReturnType<typeof setInterval> = null

  const presenterWindow = shallowRef<null | Window>(null)

  // ── Aggregate connection state ──────────────────────────
  const connected = computed(() => {
    if (viewMode.value === 'controller') {
      return bcPresenterReady.value || peerTransport.connectedCount.value > 0
    }
    return connectedViaPeer.value || bcPresenterReady.value
  })

  // ── Capabilities ────────────────────────────────────────
  const presentationSupported = computed(() => import.meta.client)
  const shouldAutoFullscreenOnLaunch = computed(() => {
    const autoFullscreenInDev = runtimeConfig.public.showdownPresentationAutoFullscreenInDev === true
    return !import.meta.dev || autoFullscreenInDev
  })

  // ── Identifier resolution ───────────────────────────────
  function ensureControllerSessionId(): string {
    if (!sessionId.value && import.meta.client) {
      sessionId.value = crypto.randomUUID()
    }
    return sessionId.value
  }

  function ensureControllerPairingCode(): string {
    if (!pairingCode.value && import.meta.client) {
      pairingCode.value = generatePairingCode()
    } else if (pairingCode.value && !isValidPairingCode(pairingCode.value) && import.meta.client) {
      pairingCode.value = generatePairingCode()
    }
    return pairingCode.value
  }

  function regeneratePairingCode() {
    if (!import.meta.client) {
      return ''
    }
    pairingCode.value = generatePairingCode()
    return pairingCode.value
  }

  const currentSessionId = computed(() => {
    if (viewMode.value === 'presenter') {
      const querySession = route.query.session
      return typeof querySession === 'string' ? querySession : ''
    }
    return ensureControllerSessionId()
  })

  // ── URL builders ────────────────────────────────────────
  function buildPresenterUrlWithSession(id: string, autoFullscreen: boolean): string {
    const url = new URL(window.location.href)
    url.searchParams.set('session', id)
    url.searchParams.set('view', 'presenter')
    url.searchParams.delete('code')
    if (autoFullscreen) {
      url.searchParams.set('autofs', '1')
    } else {
      url.searchParams.delete('autofs')
    }
    return url.toString()
  }

  // Short redirect path: `/<presenterChar><code>` → presenter view auto-paired
  // to <code>. The single-char prefix keeps the QR payload and "type this on
  // the other device" hint as tight as possible. Lowercased on the way out so
  // the URL reads cleaner; the route's normalizePairingCode upper-cases on the
  // way in for matching.
  function buildShortPairingUrl(code: string): string {
    return `${window.location.origin}/${presenterChar}${encodeURIComponent(code.toLowerCase())}`
  }

  const pairingUrl = computed(() => {
    if (!import.meta.client || viewMode.value !== 'controller') {
      return ''
    }
    const code = ensureControllerPairingCode()
    return code ? buildShortPairingUrl(code) : ''
  })

  // "Type this on the other device" URL — same short pairing URL with the
  // code embedded, so the user doesn't have to type the code separately.
  const presenterEntryUrl = computed(() => {
    if (!import.meta.client || viewMode.value !== 'controller') {
      return ''
    }
    const code = ensureControllerPairingCode()
    return code ? buildShortPairingUrl(code) : ''
  })

  // Popup-specific connection state — used to decide whether the "Close
  // Presentation Window" action is meaningful. A WebRTC presenter being
  // connected does not count.
  const popupPresenterConnected = computed(() => {
    return !!presenterWindow.value && !presenterWindow.value.closed && bcPresenterReady.value
  })

  const peerPresentersCount = peerTransport.connectedCount

  // ── BroadcastChannel wiring ─────────────────────────────
  function getChannelName(id: string): string {
    return `sfg.${peerNamespace}.presentation.${id}`
  }

  function parseBroadcastMessage(payload: unknown): null | PresentationMessage<TState> {
    if (!payload || typeof payload !== 'object') {
      return null
    }
    const type = (payload as { type?: unknown }).type
    if (type === 'controller-disconnect' || type === 'presenter-ready') {
      return { type }
    }
    if (type === 'sync-state') {
      const state = (payload as { state?: TState }).state
      if (state === undefined || state === null) {
        return null
      }
      return { state, type }
    }
    return null
  }

  function postBroadcast(message: PresentationMessage<TState>) {
    channel.value?.postMessage(message)
  }

  function handleChannelMessage(event: Event) {
    const message = parseBroadcastMessage((event as MessageEvent<unknown>).data)
    if (!message) {
      return
    }

    if (message.type === 'presenter-ready' && viewMode.value === 'controller') {
      bcPresenterReady.value = true
      lastError.value = null
      postBroadcast({ state: store.getSyncState(), type: 'sync-state' })
      return
    }

    if (message.type === 'controller-disconnect' && viewMode.value === 'presenter') {
      bcPresenterReady.value = false
      return
    }

    if (message.type === 'sync-state' && viewMode.value === 'presenter') {
      store.applySyncState(message.state)
      bcPresenterReady.value = true
    }
  }

  function ensureChannel(id: string): boolean {
    if (!import.meta.client || !id) {
      return false
    }
    if (channel.value && channelSessionId.value === id) {
      return true
    }
    tearDownChannel()
    channel.value = new BroadcastChannel(getChannelName(id))
    channel.value.addEventListener('message', handleChannelMessage)
    channelSessionId.value = id
    return true
  }

  function tearDownChannel() {
    if (channel.value) {
      channel.value.removeEventListener('message', handleChannelMessage)
      channel.value.close()
      channel.value = null
    }
    channelSessionId.value = ''
    bcPresenterReady.value = false
    clearSendQueue()
  }

  // ── Outbound state broadcasting (controller → presenters) ───────────────
  function clearSendQueue() {
    pendingState = null
    if (sendTimeout !== null) {
      clearTimeout(sendTimeout)
      sendTimeout = null
    }
  }

  function broadcastControllerState(state: TState) {
    if (viewMode.value !== 'controller') {
      return
    }
    if (bcPresenterReady.value && channel.value) {
      postBroadcast({ state, type: 'sync-state' })
    }
    if (peerTransport.connectedCount.value > 0) {
      peerTransport.send({ state, type: 'sync-state' })
    }
  }

  function queueControllerState(nextState: TState) {
    pendingState = nextState
    if (sendTimeout !== null) {
      return
    }
    sendTimeout = setTimeout(() => {
      sendTimeout = null
      if (!pendingState) {
        return
      }
      broadcastControllerState(pendingState)
      pendingState = null
    }, 75)
  }

  // ── Presenter heartbeat (BroadcastChannel only) ─────────
  // PeerJS handles its own keep-alive via the data-channel ping; only the BC
  // transport needs an application-level heartbeat so the controller knows a
  // same-device presenter is still alive.
  function clearPresenterHeartbeat() {
    if (presenterHeartbeat !== null) {
      clearInterval(presenterHeartbeat)
      presenterHeartbeat = null
    }
  }

  function startPresenterHeartbeat() {
    if (presenterTransportKind.value !== 'broadcast') {
      return
    }
    clearPresenterHeartbeat()
    postBroadcast({ type: 'presenter-ready' })
    presenterHeartbeat = setInterval(() => {
      postBroadcast({ type: 'presenter-ready' })
    }, 1200)
  }

  // ── Popup window lifecycle ──────────────────────────────
  function clearPresenterWindowPolling() {
    if (presenterWindowPolling !== null) {
      clearInterval(presenterWindowPolling)
      presenterWindowPolling = null
    }
  }

  function releasePresenterWindow() {
    presenterWindow.value = null
    if (sharedPresenterWindow) {
      sharedPresenterWindow.value = null
    }
  }

  function startPresenterWindowPolling() {
    clearPresenterWindowPolling()
    presenterWindowPolling = setInterval(() => {
      const activeWindow = presenterWindow.value
      if (!activeWindow) {
        return
      }
      if (activeWindow.closed) {
        releasePresenterWindow()
        bcPresenterReady.value = false
        clearPresenterWindowPolling()
      }
    }, 500)
  }

  function getPopupPlacement(targetScreen: PlacementScreenLike) {
    const left = Math.round(targetScreen.availLeft ?? targetScreen.left)
    const top = Math.round(targetScreen.availTop ?? targetScreen.top)
    const width = Math.round(targetScreen.availWidth ?? targetScreen.width)
    const height = Math.round(targetScreen.availHeight ?? targetScreen.height)
    return { height, left, top, width }
  }

  function placePopupOnScreen(popup: Window, targetScreen: PlacementScreenLike) {
    const { height, left, top, width } = getPopupPlacement(targetScreen)
    popup.moveTo(left, top)
    popup.resizeTo(width, height)
  }

  async function getPresentationScreen(): Promise<null | PlacementScreenLike> {
    if (!import.meta.client) {
      return null
    }
    const browserWindow = window as WindowWithPlacement
    if (!browserWindow.getScreenDetails) {
      return null
    }
    try {
      const details = await browserWindow.getScreenDetails()
      const secondary = details.screens.find(screen => screen !== details.currentScreen)
      return secondary ?? null
    } catch {
      return null
    }
  }

  async function requestPopupFullscreen(
    popup: Window,
    targetScreen: null | PlacementScreenLike,
  ): Promise<boolean> {
    try {
      const rootElement = popup.document?.documentElement
      if (!rootElement || typeof rootElement.requestFullscreen !== 'function') {
        return false
      }
      const fullscreenOptions: FullscreenOptionsWithScreen = {
        navigationUI: 'hide',
      }
      if (targetScreen) {
        fullscreenOptions.screen = targetScreen
      }
      await rootElement.requestFullscreen(fullscreenOptions)
      return true
    } catch {
      return false
    }
  }

  function waitForPresenterReady(timeoutMs: number): Promise<boolean> {
    if (connected.value) {
      return Promise.resolve(true)
    }
    return new Promise(resolve => {
      const startedAt = Date.now()
      const timer = setInterval(() => {
        if (connected.value) {
          clearInterval(timer)
          resolve(true)
          return
        }
        if (Date.now() - startedAt >= timeoutMs) {
          clearInterval(timer)
          resolve(false)
        }
      }, 120)
    })
  }

  async function launchPresentationWindow(id: string): Promise<boolean> {
    if (!import.meta.client) {
      return false
    }
    const popup = window.open(
      'about:blank',
      `sfg-${peerNamespace}-presenter`,
      'popup=yes,width=1600,height=900,left=50,top=50',
    )
    if (!popup) {
      lastError.value = 'Popup blocked. Allow popups, then try again.'
      return false
    }

    presenterWindow.value = popup
    if (sharedPresenterWindow) {
      sharedPresenterWindow.value = popup
    }
    popup.focus()
    startPresenterWindowPolling()

    const presentationScreen = await getPresentationScreen()
    if (presentationScreen) {
      placePopupOnScreen(popup, presentationScreen)
    }

    await requestPopupFullscreen(popup, presentationScreen)
    popup.location.replace(buildPresenterUrlWithSession(id, false))
    return true
  }

  async function connectPresentationAsWindow(): Promise<boolean> {
    if (!import.meta.client || viewMode.value !== 'controller') {
      return false
    }

    connecting.value = true
    lastError.value = null

    try {
      const id = crypto.randomUUID()
      sessionId.value = id

      if (!ensureChannel(id)) {
        lastError.value = 'Presentation channel is unavailable in this browser.'
        return false
      }

      bcPresenterReady.value = false

      const launched = await launchPresentationWindow(id)
      if (!launched) {
        return false
      }

      await waitForPresenterReady(4000)
      return true
    } catch (error: unknown) {
      lastError.value = error instanceof Error ? error.message : 'Unable to start presentation.'
      return false
    } finally {
      connecting.value = false
    }
  }

  // Closes the same-device popup presentation (BroadcastChannel) but keeps the
  // PeerJS peer alive so remote presenters can still join via pairing code.
  function disconnectPresentation() {
    if (viewMode.value === 'controller') {
      postBroadcast({ type: 'controller-disconnect' })
    }

    clearPresenterHeartbeat()
    clearPresenterWindowPolling()

    if (presenterWindow.value && !presenterWindow.value.closed) {
      presenterWindow.value.close()
    }
    releasePresenterWindow()

    tearDownChannel()
  }

  // Kick any connected peer (WebRTC) presenters and regenerate the pairing
  // code so the kicked presenter can't auto-reconnect with the stale code.
  // Popup flow is untouched.
  function disconnectPeerPresenters() {
    if (viewMode.value !== 'controller') {
      return
    }
    peerTransport.send({ type: 'controller-disconnect' })
    peerTransport.stop()
    regeneratePairingCode()
    // startController will re-register under the new code via the
    // pairingCode watcher.
  }

  // Presenter-initiated leave — closes the channel(s) and either closes the
  // popup window (broadcast/popup mode) or returns the tab to the pairing
  // entry screen (peer mode).
  function presenterLeave() {
    if (viewMode.value !== 'presenter') {
      return
    }
    const transportKind = presenterTransportKind.value
    peerTransport.stop()
    tearDownChannel()
    connectedViaPeer.value = false
    bcPresenterReady.value = false

    if (!import.meta.client) {
      return
    }

    // Popup mode: close the window so the parent controller's poll detects
    // the close and flips its connected state. window.close() only works on
    // windows opened by script (window.opener is set), which is always the
    // case for our broadcast popup.
    if (transportKind === 'broadcast' && window.opener) {
      window.close()
      return
    }

    // Peer mode (or popup without opener for some reason): drop all presenter
    // query params so the page reverts to the controller setup.
    const url = new URL(window.location.href)
    url.searchParams.delete('code')
    url.searchParams.delete('session')
    url.searchParams.delete('autofs')
    url.searchParams.delete('view')
    const search = url.searchParams.toString()
    const target = search ? `${url.pathname}?${search}` : url.pathname
    void navigateTo(target, { replace: true })
  }

  // Fully tear down both transports. Used on unmount / navigation away.
  function teardownAll() {
    if (viewMode.value === 'controller') {
      peerTransport.send({ type: 'controller-disconnect' })
    }
    disconnectPresentation()
    peerTransport.stop()
    connectedViaPeer.value = false
  }

  async function requestPresenterFullscreenIfNeeded() {
    if (!import.meta.client || viewMode.value !== 'presenter') {
      return
    }
    if (route.query.autofs !== '1' || document.fullscreenElement) {
      return
    }
    document.documentElement.requestFullscreen().catch(() => {})
  }

  // ── Lifecycle & watchers ────────────────────────────────
  function setupControllerTransports() {
    // BC: only bound if a same-device popup explicitly opened — we defer
    // ensureChannel to connectPresentationAsWindow so an idle controller
    // doesn't create an empty channel.
    // Peer: always boot on the controller so a cross-device presenter can
    // connect via code at any time, even mid-game.
    void startControllerPeer()
  }

  async function startControllerPeer() {
    if (!import.meta.client || viewMode.value !== 'controller') {
      return
    }
    ensureControllerPairingCode()
    await peerTransport.startController()
  }

  function bindPresenterChannel() {
    const id = currentSessionId.value
    if (!id || !ensureChannel(id)) {
      return
    }
    bcPresenterReady.value = false
    startPresenterHeartbeat()
  }

  function bindPresenterPeer() {
    if (!presenterPeerTargetCode.value) {
      return
    }
    void peerTransport.connectToController()
  }

  function resetControllerChannel() {
    clearPresenterHeartbeat()
    clearPresenterWindowPolling()
    releasePresenterWindow()
    tearDownChannel()
  }

  if (import.meta.client) {
    watch(
      () => store.getSyncState(),
      nextState => {
        if (viewMode.value === 'controller' && connected.value) {
          queueControllerState(nextState)
        }
      },
      { deep: true },
    )

    function applyViewModeSetup(mode: GameViewMode) {
      if (mode === 'controller') {
        resetControllerChannel()
        // Presenter may have left us with a random-id peer — drop it before
        // registering as the controller-id peer.
        peerTransport.stop()
        setupControllerTransports()
        return
      }

      // Presenter mode — we must stop any controller-registered peer that got
      // started during the brief pre-hydration `viewMode === 'controller'`
      // window, otherwise we'd hold the controller's peer ID from a presenter
      // tab and block the real controller from reclaiming it.
      resetControllerChannel()
      peerTransport.stop()
      if (presenterTransportKind.value === 'broadcast') {
        bindPresenterChannel()
      } else if (presenterTransportKind.value === 'peer') {
        bindPresenterPeer()
      }
    }

    // Not `immediate: true` — on a presenter URL, viewMode is transiently
    // 'controller' during SSR/hydration, so we want to wait for the authoritative
    // post-mount value before any transport setup runs.
    watch(viewMode, mode => applyViewModeSetup(mode))

    watch(currentSessionId, id => {
      if (viewMode.value === 'presenter' && id && presenterTransportKind.value === 'broadcast') {
        bindPresenterChannel()
      }
    })

    watch(presenterPeerTargetCode, () => {
      if (viewMode.value === 'presenter' && presenterTransportKind.value === 'peer') {
        bindPresenterPeer()
      }
    })

    // When a controller regenerates their pairing code, re-register the peer
    // under the new ID. Existing presenters are forcibly disconnected so the
    // old code becomes immediately invalid — which is the point of regenerate.
    watch(pairingCode, () => {
      if (viewMode.value !== 'controller') {
        return
      }
      peerTransport.stop()
      void peerTransport.startController()
    })

    watch(() => bcPresenterReady.value, isReady => {
      if (viewMode.value !== 'presenter' || presenterTransportKind.value !== 'broadcast') {
        return
      }
      if (isReady) {
        clearPresenterHeartbeat()
      } else {
        startPresenterHeartbeat()
      }
    })

    watch(connected, isNowConnected => {
      if (isNowConnected && viewMode.value === 'controller') {
        // Bootstrap whichever presenter(s) just connected with current state.
        broadcastControllerState(store.getSyncState())
      }
    })

    watch(() => route.query.autofs, () => {
      void requestPresenterFullscreenIfNeeded()
    })

    onMounted(() => {
      applyViewModeSetup(viewMode.value)
      void requestPresenterFullscreenIfNeeded()
    })

    // Release the PeerJS ID promptly on tab close so a refresh can reclaim it
    // before the signaling server's stale-peer timeout expires.
    const handleBeforeUnload = () => {
      peerTransport.stop()
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', handleBeforeUnload)
    }

    onBeforeUnmount(() => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', handleBeforeUnload)
      }
      teardownAll()
    })
  }

  // Presenter-side: ask the peer transport to retry now instead of waiting
  // for the next scheduled backoff tick.
  function presenterReconnect() {
    if (viewMode.value !== 'presenter') {
      return
    }
    void peerTransport.forcePresenterReconnect()
  }

  return {
    connected,
    connecting,
    connectPresentationAsWindow,
    disconnectPeerPresenters,
    disconnectPresentation,
    lastError,
    pairingCode,
    pairingUrl,
    peerPresentersCount,
    peerReady: peerTransport.isReady,
    peerTransportConnecting: peerTransport.isConnecting,
    popupPresenterConnected,
    presentationSupported,
    presenterEntryUrl,
    presenterLeave,
    presenterReconnect,
    presenterTargetCode: presenterPeerTargetCode,
    presenterTransportKind,
    regeneratePairingCode,
    shouldAutoFullscreenOnLaunch,
  }
}
